import { Button, Color, Icon, SkeletonBox, Text, Tooltip } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ReviewEntryTableRow } from '../../types/reviewEntryTableRow'
import { getReviewEntriesGlobalStatus } from '../../utils/getReviewEntriesGlobalStatus'
import * as Styled from './styles'

interface ReviewEntriesGlobalStatusProps {
  autoValidatableCount?: number
  isError: boolean
  isLoading: boolean
  isValidatingAll?: boolean
  onValidateAllAutomatched?: () => void
  rows: ReviewEntryTableRow[]
}

export const ReviewEntriesGlobalStatus = ({
  autoValidatableCount = 0,
  isError,
  isLoading,
  isValidatingAll,
  onValidateAllAutomatched,
  rows,
}: ReviewEntriesGlobalStatusProps): ReactElement | null => {
  const { t } = useTranslation()
  const status = useMemo(() => getReviewEntriesGlobalStatus(rows), [rows])

  const subtitle = useMemo(
    () =>
      [
        autoValidatableCount > 0
          ? t('review_entries.global_status.automatched', { count: autoValidatableCount })
          : null,
        status.needsClarificationCount > 0
          ? t('review_entries.global_status.needs_clarification', { count: status.needsClarificationCount })
          : null,
        status.newSuppliersCount > 0
          ? t('review_entries.global_status.new_suppliers', { count: status.newSuppliersCount })
          : null,
      ]
        .filter(Boolean)
        .join(', '),
    [autoValidatableCount, status.needsClarificationCount, status.newSuppliersCount, t],
  )
  const isAllValidated = status.toValidateCount === 0
  const isValidateAllDisabled = Boolean(isValidatingAll) || autoValidatableCount === 0

  if (isError) {
    return null
  }

  if (isLoading) {
    return (
      <Styled.LoadingWrapper>
        <SkeletonBox fullWidth height={72} />
      </Styled.LoadingWrapper>
    )
  }

  const validateAllButton = onValidateAllAutomatched ? (
    <Button disabled={isValidateAllDisabled} loading={isValidatingAll} onClick={onValidateAllAutomatched} size="sm">
      {t('review_entries.global_status.validate_all_automatched')}
    </Button>
  ) : null

  return (
    <Styled.GlobalStatusCard>
      <Styled.IconTile isSuccess={isAllValidated}>
        <Icon
          color={isAllValidated ? Color.Green700 : Color.Purple600}
          icon={isAllValidated ? 'check' : 'sparkle'}
          size="sm"
        />
      </Styled.IconTile>
      <Styled.Content>
        <Text variant="bodyMD" weight="medium">
          {isAllValidated
            ? t('review_entries.global_status.all_validated')
            : t('review_entries.global_status.title', { count: status.toValidateCount })}
        </Text>
        {subtitle ? (
          <Text colorVariant="secondary" variant="bodySM">
            {subtitle}
          </Text>
        ) : null}
      </Styled.Content>
      {validateAllButton && (
        <Styled.Actions>
          {isValidateAllDisabled && !isValidatingAll ? (
            <Tooltip label={t('review_entries.global_status.validate_all_automatched.disabled')} placement="top">
              {validateAllButton}
            </Tooltip>
          ) : (
            validateAllButton
          )}
        </Styled.Actions>
      )}
    </Styled.GlobalStatusCard>
  )
}
